import { BriefDiffusionStatus, ErrorCode } from '@fluenceur/types';
import type { BriefTemplateContent, BriefTemplateResponse } from '@fluenceur/types';
import { isAxiosError } from 'axios';
import { isApiErrorCode } from '@/libs/api-error';

/** What the server says a brief still lacks before it can open (`data.missing`). */
export type BriefOpeningGap =
  | 'deliverables'
  | 'timeline'
  | 'compensation'
  | 'usage_rights'
  | 'delivery';

/** Section title naming each gap, so the error toast speaks the editor's words. */
export const OPENING_GAP_LABEL_KEYS: Record<BriefOpeningGap, string> = {
  deliverables: 'collabs.briefTemplates.sections.deliverables.title',
  timeline: 'collabs.briefTemplates.sections.timeline.title',
  compensation: 'collabs.briefTemplates.sections.compensation.title',
  usage_rights: 'collabs.briefTemplates.sections.usageRights.title',
  delivery: 'collabs.briefTemplates.sections.delivery.title',
};

/**
 * Applications are reserved for briefs that pay or gift a product. This is the
 * only gap the editor can see coming without asking the server, so it is the
 * one that disables the opening controls; the others come back from the call.
 */
export const isDiffusionEligible = (content: BriefTemplateContent): boolean =>
  content.compensation.type !== 'none';

/**
 * A brief the brand has never finished nor opened: the editor proposes to open
 * it on Finish by default. Any brief that was finished once keeps its old
 * behaviour unless the brand asks.
 */
export const isNeverFinishedBrief = (
  template: Pick<BriefTemplateResponse, 'diffusion' | 'lastVersionNumber'>
): boolean =>
  template.diffusion.status === BriefDiffusionStatus.OFF && template.lastVersionNumber === null;

const isOpeningGap = (value: unknown): value is BriefOpeningGap =>
  typeof value === 'string' && value in OPENING_GAP_LABEL_KEYS;

/**
 * The gaps of an `E_BRIEF_NOT_OPENABLE` refusal, or null for any other error.
 * Unknown gap names (shipped after this build) are dropped rather than shown raw.
 */
export const openingGapsOf = (error: unknown): BriefOpeningGap[] | null => {
  if (!isApiErrorCode(error, ErrorCode.E_BRIEF_NOT_OPENABLE) || !isAxiosError(error)) return null;
  const missing = (error.response?.data as { data?: { missing?: unknown } } | undefined)?.data
    ?.missing;
  return Array.isArray(missing) ? missing.filter(isOpeningGap) : [];
};

/** "14 oct. 2026": the day a brief was opened or stopped, in the UI locale. */
export const formatDiffusionDate = (iso: string | null, locale: string): string =>
  iso
    ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
        new Date(iso)
      )
    : '';

/**
 * Every application of a brief, the answered ones too: the entries linking here
 * promise the count the brief shows, not the pending ones.
 */
export const briefApplicationsPath = (templateId: string): string =>
  `/applications?targetIds=${templateId}&applicationStatus=all`;
